import {
  IMenhirOperation,
  IMenhirOperationChildcare,
  IMenhirOperationFailedExpense,
  IMenhirOperationItem,
  IMenhirOperationReimbursementExpense,
  IMenhirOperationReimbursementOrder,
  IMenhirOperationTopUp,
  IMenhirOperationVoucher,
  IMenhirOperationVoucherExpense,
  IOperation,
  IOperationChildcare,
  IOperationItem,
  IOperationReimbursementExpense,
  IOperationReimbursementOrder,
  IOperationTopUp,
  IOperationVoucher,
  IOperationVoucherExpense,
  TMenhirOperationPeriod,
  TOperationPeriod,
} from '@/common/integrations/menhir/employee/operations/operations.types';
import { transformReimbursementBeneficiary } from '@/common/integrations/menhir/employee/reimbursements/reimbursements.dto';
import { PaginationResponse } from '@/common/types/general';
import { IMenhirPaginationResponse } from '@/common/types/menhir/general';
import { transformFiles } from '@/common/utils/dto';

export const transformOperationPeriodsPaginatedResponse = (
  response: IMenhirPaginationResponse<TMenhirOperationPeriod>
): PaginationResponse<TOperationPeriod> => {
  return {
    totalResults: response.total_results,
    page: response.current_page,
    perPage: response.results_per_page,
    totalPages: response.total_pages,
    list: response.list.map((period) => ({
      id: period.id,
      startsOn: period.starts_on,
      endsOn: period.ends_on,
    })),
  };
};

const isTopUpOperation = (
  operation: IMenhirOperation
): operation is IMenhirOperationTopUp => operation.type === 'topup';

const isVoucherExpenseOperation = (
  operation: IMenhirOperation
): operation is IMenhirOperationVoucherExpense =>
  operation.type === 'benefit_expense' && 'voucher' in operation;

const isReimbursementExpenseOperation = (
  operation: IMenhirOperation
): operation is IMenhirOperationReimbursementExpense =>
  operation.type === 'benefit_expense' && 'reimbursement_order' in operation;

export const isChildcareOperation = (
  operation: IMenhirOperation
): operation is IMenhirOperationChildcare => 'childcare_voucher' in operation;

export const isExpenseFailedOperation = (
  operation: IMenhirOperation
): operation is IMenhirOperationFailedExpense => 'error_tag' in operation;

export const transformOperationShared = (
  menhirOperation: IMenhirOperation
): IOperation => ({
  id: menhirOperation.id,
  type: menhirOperation.type,
  status: menhirOperation.status,
  amount: menhirOperation.amount,
  executedAt: menhirOperation.executed_at,
  isDebit: menhirOperation.is_debit,
  usedEmployeeCreditAmount: menhirOperation.used_employee_credit_amount,
  descriptionTag: menhirOperation.description_tag,
  descriptionParams: menhirOperation.description_params,
  categorySlug: menhirOperation.category_slug,
  productSlug: menhirOperation.product_slug,
});

export const transformOperationVoucher = (
  voucher: IMenhirOperationVoucher
): IOperationVoucher => ({
  type: voucher.type,
  count: voucher.count,
  amount: voucher.amount,
});

export const transformOperationTopUp = (
  operation: IMenhirOperationTopUp
): IOperationTopUp => ({
  ...transformOperationShared(operation),
  type: 'topup',
  voucher: transformOperationVoucher(operation.voucher),
});

export const transformOperationVoucherExpense = (
  operation: IMenhirOperationVoucherExpense
): IOperationVoucherExpense => ({
  ...transformOperationShared(operation),
  type: 'benefit_expense',
  voucher: transformOperationVoucher(operation.voucher),
  merchantName: operation.merchant_name,
  usedVoucherAmount: operation.used_voucher_amount,
});

export const transformOperationReimbursementOrder = (
  order: IMenhirOperationReimbursementOrder
): IOperationReimbursementOrder => ({
  id: order.id,
  amount: order.amount,
  status: order.status,
  purchasedAt: order.purchased_at,
  insertedAt: order.inserted_at,
  iban: order.bank_account.number,
  fiscalYear: order.fiscal_year,
  invoiceNumber: order.invoice_number,
  educationLevel: order.education_level,
  beneficiary: order.beneficiary_person
    ? transformReimbursementBeneficiary(order.beneficiary_person)
    : undefined,
  type: order.type,
  files: transformFiles(order.files) ?? [],
  reference: order.reference,
  statusDetails: order.status_details
    ? {
        date: order.status_details.at,
        reason: order.status_details.reason,
        updatedBy: order.status_details.by,
      }
    : undefined,
});

export const transformOperationReimbursementExpense = (
  operation: IMenhirOperationReimbursementExpense
): IOperationReimbursementExpense => ({
  ...transformOperationShared(operation),
  type: 'benefit_expense',
  reimbursementOrder: transformOperationReimbursementOrder(
    operation.reimbursement_order
  ),
});

export const transformOperationChildcare = (
  operation: IMenhirOperationChildcare
): IOperationChildcare => ({
  ...transformOperationShared(operation),
  childcareVoucher: {
    id: operation.childcare_voucher.id,
  },
});

export const transformOperation = (operation: IMenhirOperation): IOperation => {
  if (isTopUpOperation(operation)) {
    return transformOperationTopUp(operation);
  }

  if (isChildcareOperation(operation)) {
    return transformOperationChildcare(operation);
  }
  if (isVoucherExpenseOperation(operation)) {
    return transformOperationVoucherExpense(operation);
  }
  if (isReimbursementExpenseOperation(operation)) {
    return transformOperationReimbursementExpense(operation);
  }

  return transformOperationShared(operation);
};

export const transformOperationList = (
  response: IMenhirPaginationResponse<IMenhirOperationItem>
): PaginationResponse<IOperationItem> => ({
  totalResults: response.total_results,
  page: response.current_page,
  perPage: response.results_per_page,
  totalPages: response.total_pages,
  list: response.list.map(transformOperationShared),
});
